/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { Api } from 'app/shared/lib/api/api';
import { IApiActionResults, IApiBatchProgress, IApiQueryResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { CarePlanGoalWithLocalState } from 'app/cm/models/local-state-extensions';
import * as CM from 'app/cm/models/essette-api';
import * as Core from 'app/core/models/essette-api';

@autoinject
export class CarePlanService {

    ////****************** Fields and Properties ************************************////


    ////****************** Functions ************************************************////

    constructor(public api: Api) { }


    ////****************** Queries **************************************************////
    public async loadCaseCarePlan(caseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanGoal>> {
        const query = this.api
            .query(`cm/cases/${caseId}/careplan/goals`)
            .applyStandardLoadOptions(options, { applyPaginationSizeToDetails: true })
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadHouseholdCaseCarePlan(householdCaseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CarePlanGoal>> {
        const query = this.api
            .query(`cm/households/cases/${householdCaseId}/careplan/goals`)
            .applyStandardLoadOptions(options, { applyPaginationSizeToDetails: true })
            .includeWithState(options, 'interventions', 'barriers', 'outcomes');

        includes.forEach(i => {
            if(i === 'interventions' || i === 'barriers' || i === 'outcomes') {
                return;
            }
            query.include(i);
        });

        return await this.api.get(query);
    }

    public async loadGoalsByHouseholdCase(householdCaseId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CarePlanGoal>> {
        const query = this.api
            .query(`cm/households/cases/${householdCaseId}/careplan/goals`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadCarePlanGoalPriorities(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanGoalPriority>> {
        const query = this.api
            .query('cm/careplans/goals/priorities')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadResourcesByGoalDefinition(goalDefinitionId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.Resource>> {
        const query = this.api
            .query(`cm/careplans/goals/definitions/${goalDefinitionId}/resources`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadOutcomeDefinitions(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanOutcomeDefinition>> {
        const query = this.api
            .query('cm/carePlans/outcomes/definitions')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async searchOutcomeDefinitions(searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanOutcomeDefinition>> {
        const query = this.api
            .query('cm/carePlans/outcomes/definitions')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterString('description', searchValue, { operator: 'contains' })
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadInterventionsByGoal(goalId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CM.CarePlanIntervention>> {
        const query = this.api
            .query(`cm/careplans/goals/${goalId}/interventions`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadBarriersByGoal(goalId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanBarrier>> {
        const query = this.api
            .query(`cm/careplans/goals/${goalId}/barriers`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadOutcomesByGoal(goalId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanOutcome>> {
        const query = this.api
            .query(`cm/careplans/goals/${goalId}/outcomes`)
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async searchGoalDefinitions(searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], includeSystem: boolean = false, fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanGoalDefinition>> {
        const query = this.api
            .query('cm/careplans/goals/definitions')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterString('description', searchValue, { operator: 'contains' })
            .field(...fields);

        if(!includeSystem) {
            query.filterBoolean('isSystem', false)
        }

        return await this.api.get(query);
    }

    public async searchBarrierDefinitions(searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanBarrierDefinition>> {
        const query = this.api
            .query('cm/carePlans/barriers/definitions')
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .filterString('description', searchValue, { operator: 'contains' })
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadBarrierDefinitions(options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanBarrierDefinition>> {
        const query = this.api
            .query('cm/carePlans/barriers/definitions')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadGoalDefinitions(options?: Partial<IQueryLoadOptions>, includes: string[] = [], includeSystem: boolean = false, fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanGoalDefinition>> {
        const query = this.api
            .query('cm/careplans/goals/definitions')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(!includeSystem) {
            query.filterBoolean('isSystem', false)
        }
        query.filterBoolean('isCustom', false)

        return await this.api.get(query);
    }

    public async loadCustomInterventionDefinition(): Promise<IApiQueryResults<CM.CarePlanInterventionDefinition>> {
        const query = this.api
            .query('cm/careplans/interventions/definitions')
            .filterBoolean('isCustom', true);

        return await this.api.get(query);
    }


    ////****************** Loads ****************************************************////

    public async loadGoal(goalId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanGoal>> {
        const query = this.api
            .query(`cm/careplans/goals/${goalId}`)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }
    public async loadGoalDefinition(definitionId: string, includes: string[] = []): Promise<IApiQueryResults<CM.CarePlanGoal>> {
        const query = this.api
            .query(`cm/careplans/goals/definitions/${definitionId}`)
            .include(...includes);
        return await this.api.get(query);
    }
    public async loadBarrier(barrierId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanBarrier>> {
        const query = this.api
            .query(`cm/careplans/barriers/${barrierId}`)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }
    public async loadOutcome(outcomeId: string, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CM.CarePlanOutcome>> {
        const query = this.api
            .query(`cm/careplans/outcomes/${outcomeId}`)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }


    ////****************** Actions **************************************************////

    public async insertCarePlanProblem(carePlanProblem: CM.CarePlanProblem): Promise<IApiActionResults> {
        const action = this.api.action("insert", 'cm/careplans/problems', carePlanProblem)
        return await this.api.post(action);
    }

    /**
     * Apply multiple problems in one api call
     * @param problems array of care plan problems
     * @returns array of results
     */
    public async insertCarePlanProblemsBatch(problems: CM.CarePlanProblem[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        const batch = this.api.batch();
        for(const p of problems) {
            batch.post(this.api.action('insert', 'cm/careplans/problems', p));
        }
        return await this.api.execute(batch, { progress });
    }

    public async insertCarePlanGoal(carePlanGoal: CM.CarePlanGoal): Promise<IApiActionResults> {
        const action = this.api.action("insert", 'cm/careplans/goals', carePlanGoal);

        return await this.api.post(action);
    }

    /**
     * Apply multiple goals to multiple cases
     * @param goals array of care plan goals
     * @returns array of results
     */
    public async insertCarePlanGoalsToCasesBatch(goals: CarePlanGoalWithLocalState[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        const batch = this.api.batch();
        for(const g of goals) {
            batch.post(this.api.action('insert', 'cm/careplans/goals', g));
        }
        return await this.api.execute(batch, { progress });
    }

    public async insertCarePlanGoalSummary(goalSummary: CM.CarePlanGoalSummary): Promise<IApiActionResults> {
        const action = this.api.action("insert", 'cm/careplans/goals/summaries', goalSummary);

        return await this.api.post(action);
    }
    public async updateCarePlanGoal(goalId: string, carePlanGoal: CM.CarePlanGoal): Promise<IApiActionResults> {
        const action = this.api.action('update', `cm/careplans/goals/${goalId}`, carePlanGoal);
        return await this.api.post(action);
    }
    public async completeCarePlanGoal(goalId: string): Promise<IApiActionResults> {
        const action = this.api.action('complete', `cm/careplans/goals/${goalId}`, {});
        return await this.api.post(action);
    }
    public async activateCarePlanGoal(goalId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cm/careplans/goals/${goalId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async insertCarePlanIntervention(carePlanIntervention: CM.CarePlanIntervention): Promise<IApiActionResults> {
        const action = this.api.action("insert", "cm/careplans/interventions", carePlanIntervention);
        return await this.api.post(action);
    }

    /**
   * Apply multiple interventions in one api call
   * @param interventions array of care plan interventions
   * @returns array of results
   */
    public async insertCarePlanInterventionsBatch(interventions: CM.CarePlanIntervention[], progress?: IApiBatchProgress): Promise<IApiActionResults[]> {
        const batch = this.api.batch();
        for(const i of interventions) {
            batch.post(this.api.action('insert', 'cm/careplans/interventions', i));
        }
        return await this.api.execute(batch, { progress });
    }

    public async activateCarePlanIntervention(interventionId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cm/careplans/interventions/${interventionId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async insertCarePlanBarrier(carePlanBarrier: CM.CarePlanBarrier): Promise<IApiActionResults> {
        const action = this.api.action('insert', 'cm/careplans/barriers', carePlanBarrier);

        return await this.api.post(action);
    }

    public async activateCarePlanBarrier(barrierId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cm/careplans/barriers/${barrierId}`, { isActive: isActive });
        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }

    public async insertCarePlanOutcome(carePlanOutcome: CM.CarePlanOutcome): Promise<IApiActionResults> {
        const action = this.api.action("insert", "cm/careplans/outcomes", carePlanOutcome);

        return await this.api.post(action);
    }

    public async activateCarePlanOutcome(outcomeId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cm/careplans/outcomes/${outcomeId}`, { isActive: isActive });
        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }
}
